import { DataTable } from '@peritasai/ui-components';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium';
import type { LocationEntity } from '@peritasai/repository';
import { useTranslation } from 'react-i18next';
import { renderTableCell } from '../shared/renderTableCell';

const getColumns = (t: (key: string) => string): GridColDef<LocationEntity>[] => {
  const naText = t('pages.entityManagement.table.naPlaceholder');
  const renderCell = (value: unknown, bold = false) => renderTableCell(value, bold, naText);

  return [
  {
    field: 'name',
    headerName: t('pages.entityManagement.locationsTable.columnName'),
    flex: 1.5,
    renderCell: (params) => renderCell(params.value, true),
  },
  {
    field: 'departmentOrUnit',
    headerName: t('pages.entityManagement.locationsTable.columnDepartmentOrUnit'),
    flex: 1.5,
    renderCell: (params) => renderCell(params.value),
  },
  {
    field: 'additionalDetails',
    headerName: t('pages.entityManagement.locationsTable.columnAdditionalDetails'),
    flex: 1.5,
    renderCell: (params) => renderCell(params.value),
  },
  {
    field: 'floor',
    headerName: t('pages.entityManagement.locationsTable.columnFloor'),
    flex: 1,
    renderCell: (params) => renderCell(params.value),
  },
  {
    field: 'facilityName',
    headerName: t('pages.entityManagement.locationsTable.columnFacility'),
    flex: 1.5,
    renderCell: (params) => renderCell(params.value),
  },
  {
    field: 'locationType',
    headerName: t('pages.entityManagement.locationsTable.columnLocationType'),
    flex: 1,
    renderCell: (params) => renderCell(params.value),
  },
  ];
};

interface LocationsTableProps {
  rows: LocationEntity[];
  rowCount: number;
  isLoading: boolean;
  hasSearch: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onRowClick: (row: LocationEntity) => void;
}

export const LocationsTable = ({
  rows,
  rowCount,
  isLoading,
  hasSearch,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
}: LocationsTableProps) => {
  const { t } = useTranslation(['periverse']);
  const columns = getColumns(t);

  return (
    <DataTable
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      rowCount={rowCount}
      isLoading={isLoading}
      hasSearch={hasSearch}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      onRowClick={onRowClick}
      searchEmptyTitle={t('pages.entityManagement.table.searchEmptyTitle')}
      searchEmptyMessage={t('pages.entityManagement.table.searchEmptyMessage')}
      emptyMessage={t('pages.entityManagement.table.noRows', {
        entityName: t('pages.entityManagement.items.locations'),
      })}
      skeletonColumnCount={6}
    />
  );
};
